import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../shared/utils/api';
import PageToggle from '../../shared/components/PageToggle';
import ROUTES from '../../shared/constants/routes';
import DrillStats from './DrillStats';
import DrillSession from './DrillSession';
import TopicPicker from '../questions/TopicPicker';

const MODULES = [
  { value: 'both', label: 'Both' },
  { value: 'FLK1', label: 'FLK1' },
  { value: 'FLK2', label: 'FLK2' },
];

const MODES = [
  { value: 'weakest', label: 'Weakest topics', desc: 'Focus on your lowest-scoring areas' },
  { value: 'topic', label: 'Specific topic', desc: 'Choose an L2 area or L3 subtopic' },
  { value: 'random', label: 'Random', desc: 'Drills from across all topics' },
  { value: 'incorrect', label: 'Review incorrect', desc: 'Retry drills you got wrong' },
];

const COUNTS = [5, 10, 15, 20];

const TOGGLE_OPTS = [
  { value: 'mcq', label: 'MCQ Practice', route: ROUTES.QUESTIONS },
  { value: 'drills', label: 'Drills', route: ROUTES.DRILLS },
];

export default function DrillsPage() {
  const [stats, setStats] = useState(null);
  const [module, setModule] = useState('both');
  const [mode, setMode] = useState('weakest');
  const [count, setCount] = useState(10);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [topicCounts, setTopicCounts] = useState([]);
  const [topicCountsLoading, setTopicCountsLoading] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState(() => new Set());

  const loadStats = useCallback(() => {
    api('/api/questions/stats?question_type=drill')
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (mode !== 'topic' || topicCounts.length > 0) return;
    setTopicCountsLoading(true);
    api('/api/questions/topic-counts?question_type=drill')
      .then((d) => {
        setTopicCounts(d.topics || []);
        setTopicCountsLoading(false);
      })
      .catch(() => setTopicCountsLoading(false));
  }, [mode, topicCounts.length]);

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canStart = mode !== 'topic' || selectedTopicIds.size > 0;

  const handleStart = () => {
    setSessionConfig({
      module,
      mode,
      count,
      topicIds: Array.from(selectedTopicIds),
    });
  };

  const handleExit = () => {
    setSessionConfig(null);
    loadStats();
  };

  if (sessionConfig) {
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-4">
        <DrillSession config={sessionConfig} onExit={handleExit} />
      </div>
    );
  }

  if (mode === 'topic') {
    const pickedCount = selectedTopicIds.size;
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
        <div className="flex items-center justify-between mb-sp-5">
          <h1 className="text-2xl font-bold text-text-primary">Drills</h1>
          <PageToggle options={TOGGLE_OPTS} active="drills" />
        </div>

        <div className="bg-bg-card rounded-r-xl border border-border-soft">
          <div className="px-sp-6 py-sp-5">
            <p className="text-[15px] font-semibold text-text-primary mb-sp-4">Choose topics</p>
            {topicCountsLoading ? (
              <div className="py-sp-8 text-center text-sm text-text-muted">Loading topics</div>
            ) : (
              <TopicPicker
                topics={topicCounts}
                selectedIds={selectedTopicIds}
                onToggle={toggleTopic}
              />
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setMode('weakest');
                  setSelectedTopicIds(new Set());
                }}
                className="px-sp-3 py-sp-2 text-sm font-medium text-text-secondary border border-border-soft rounded-r-md hover:bg-bg-page transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={!canStart}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {pickedCount > 0
                  ? `Start session (${pickedCount} topic${pickedCount === 1 ? '' : 's'})`
                  : 'Start session'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
      <div className="flex items-center justify-between mb-sp-5">
        <h1 className="text-2xl font-bold text-text-primary">Drills</h1>
        <PageToggle options={TOGGLE_OPTS} active="drills" />
      </div>

      <DrillStats stats={stats} />

      <div className="bg-bg-card rounded-r-xl border border-border-soft">
        <div className="px-sp-6 py-sp-5">
          <p className="text-[15px] font-semibold text-text-primary mb-sp-4">Start a session</p>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Module</p>
          <div className="inline-flex border border-border-soft rounded-r-md overflow-hidden mb-sp-5">
            {MODULES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setModule(m.value)}
                className={`px-sp-5 py-sp-2 text-sm font-medium transition-colors ${
                  module === m.value
                    ? 'bg-accent text-white'
                    : 'bg-transparent text-text-secondary hover:bg-bg-page'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">What to practise</p>
          <div className="grid grid-cols-2 gap-sp-2 mb-sp-5">
            {MODES.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  className={`text-left rounded-r-lg px-sp-4 py-sp-3 transition-colors ${
                    active
                      ? 'border-2 border-accent bg-blue-lightest'
                      : 'border border-border-soft hover:border-border-medium'
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold mb-sp-1 ${
                      active ? 'text-accent' : 'text-text-primary'
                    }`}
                  >
                    {m.label}
                  </h3>
                  <p className="text-xs text-text-muted">{m.desc}</p>
                </button>
              );
            })}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Number of drills</p>
          <div className="inline-flex gap-sp-2 mb-sp-2">
            {COUNTS.map((n) => {
              const active = count === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCount(n)}
                  className={`w-[44px] h-[36px] rounded-[6px] text-sm font-medium transition-colors ${
                    active
                      ? 'border-2 border-accent bg-blue-lightest text-accent'
                      : 'border border-border-soft bg-bg-card text-text-muted hover:border-border-medium'
                  }`}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-text-muted mb-sp-5">
            Immediate feedback after each drill
          </p>

          <button
            type="button"
            onClick={handleStart}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
          >
            Start session
          </button>
        </div>
      </div>

      <div className="mt-sp-4 px-sp-5 py-sp-4 rounded-r-lg bg-[rgba(59,130,246,0.04)] border border-[rgba(59,130,246,0.12)] text-xs text-text-secondary leading-relaxed">
        <strong className="text-text-primary">Drills vs MCQs:</strong> Drills test your recall of
        specific legal rules without a scenario. They are designed for learning and embedding
        knowledge. MCQs test your ability to apply rules to a fact pattern, closer to exam
        conditions.
      </div>
    </div>
  );
}
